import React, { useEffect, useRef, useState } from 'react';

interface MapYandexProps {
  lat: number;
  lng: number;
  label?: string;
}

declare global {
  interface Window {
    ymaps: any;
  }
}

const MapYandex: React.FC<MapYandexProps> = ({ lat, lng, label }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const placemarkRef = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mapContainer.current) return;
    if (!lat || !lng) return;

    let isMounted = true;

    // Load Yandex Maps API if not already loaded
    if (!window.ymaps) {
      const script = document.createElement('script');
      // Note: In production, you should get the API key from environment variables
      // For now, using a placeholder - you'll need to set this up
      const apiKey = import.meta.env.VITE_YANDEX_MAPS_API_KEY || 'your_yandex_api_key';
      script.src = `https://api-maps.yandex.ru/2.1/?apikey=${apiKey}&lang=ru_RU`;
      script.async = true;
      script.onload = () => {
        if (isMounted && mapContainer.current) {
          initializeMap();
        }
      };
      script.onerror = () => {
        console.error('Failed to load Yandex Maps API');
        setError('Failed to load map');
      };
      document.head.appendChild(script);
    } else {
      initializeMap();
    }

    function initializeMap() {
      if (!window.ymaps || !mapContainer.current || !isMounted) return;

      window.ymaps.ready(() => {
        if (!isMounted || !mapContainer.current) return;

        // Create map instance
        if (!mapRef.current) {
          mapRef.current = new window.ymaps.Map(mapContainer.current, {
            center: [lat, lng],
            zoom: 13,
            controls: ['zoomControl', 'fullscreenControl']
          });
        } else {
          mapRef.current.setCenter([lat, lng]);
        }

        // Create or update placemark
        if (placemarkRef.current) {
          placemarkRef.current.geometry.setCoordinates([lat, lng]);
        } else {
          placemarkRef.current = new window.ymaps.Placemark([lat, lng], {
            balloonContent: label || 'Store Location'
          }, {
            preset: 'islands#redDotIcon'
          });
          mapRef.current.geoObjects.add(placemarkRef.current);
        }
      });
    }

    return () => {
      isMounted = false;
      if (placemarkRef.current && mapRef.current) {
        mapRef.current.geoObjects.remove(placemarkRef.current);
        placemarkRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
    };
  }, [lat, lng, label]);

  // Show error if map failed to load
  if (error) {
    return (
      <div 
        style={{ 
          width: '100%', 
          height: 300, 
          marginTop: 8, 
          borderRadius: 8, 
          border: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f5f5f5'
        }}
      >
        <div style={{ color: '#d32f2f', textAlign: 'center' }}>
          <div>Failed to load Yandex Maps</div>
          <div style={{ fontSize: '0.875rem', marginTop: 4 }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={mapContainer} 
      style={{ 
        width: '100%', 
        height: 300, 
        marginTop: 8, 
        borderRadius: 8, 
        overflow: 'hidden',
        border: '1px solid #e0e0e0'
      }} 
    />
  );
};

export default MapYandex; 